import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  Circle,
  ExternalLink,
  Copy,
  ChevronDown,
  ChevronUp,
  Calendar,
  User,
  Clock,
  Shield,
  Save,
  X,
  AlertCircle,
  History,
  Eye,
  Link2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Componente para editar una sección
function SeccionEditor({ seccion, index, onChange, onRemove, canRemove }) {
  const [expanded, setExpanded] = useState(true);

  const handleTituloChange = (e) => {
    onChange(index, { ...seccion, titulo: e.target.value });
  };

  const handleItemChange = (itemIndex, value) => {
    const newItems = [...seccion.items];
    newItems[itemIndex] = value;
    onChange(index, { ...seccion, items: newItems });
  };

  const addItem = () => {
    onChange(index, { ...seccion, items: [...seccion.items, ''] });
  };

  const removeItem = (itemIndex) => {
    if (seccion.items.length > 1) {
      const newItems = seccion.items.filter((_, i) => i !== itemIndex);
      onChange(index, { ...seccion, items: newItems });
    }
  };

  return (
    <div className="border border-zinc-700 rounded-lg overflow-hidden">
      <div 
        className="flex items-center justify-between bg-zinc-800 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-[#D4AF37] font-medium">{seccion.titulo || `Sección ${index + 1}`}</span>
          <span className="text-xs text-zinc-500">({seccion.items.length} items)</span>
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(index); }}
              className="p-1 text-red-400 hover:bg-red-900/30 rounded"
              title="Eliminar sección"
            >
              <Trash2 size={16} />
            </button>
          )}
          {expanded ? <ChevronUp size={18} className="text-zinc-400" /> : <ChevronDown size={18} className="text-zinc-400" />}
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 bg-zinc-900/50 space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Título de la sección</label>
            <input
              type="text"
              value={seccion.titulo}
              onChange={handleTituloChange}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
              placeholder="Ej: CONDICIONES DE VENTA"
            />
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Puntos/Items de esta sección</label>
            <div className="space-y-2">
              {seccion.items.map((item, itemIndex) => (
                <div key={itemIndex} className="flex gap-2">
                  <span className="text-[#D4AF37] mt-2">•</span>
                  <textarea
                    value={item}
                    onChange={(e) => handleItemChange(itemIndex, e.target.value)}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-none"
                    rows={2}
                    placeholder="Escribe el texto de este punto..."
                  />
                  {seccion.items.length > 1 && (
                    <button
                      onClick={() => removeItem(itemIndex)}
                      className="p-2 text-red-400 hover:bg-red-900/30 rounded self-start"
                      title="Eliminar punto"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addItem}
              className="mt-2 text-sm text-[#D4AF37] hover:underline flex items-center gap-1"
            >
              <Plus size={14} /> Agregar punto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Modal para crear/editar versión
function VersionModal({ version, onClose, onSave, isLoading }) {
  const [formData, setFormData] = useState({
    nombre_version: version?.nombre_version || '',
    titulo_documento: version?.titulo_documento || 'CONDICIONES DE VENTA, GARANTÍA Y RETRACTO',
    fecha_vigencia: version?.fecha_vigencia || new Date().toISOString().split('T')[0],
    secciones: version?.secciones || [
      { titulo: 'CONDICIONES DE VENTA', items: [''] },
      { titulo: 'POLÍTICA DE GARANTÍA', items: [''] },
      { titulo: 'POLÍTICA DE RETRACTO', items: [''] }
    ],
    notas_internas: version?.notas_internas || ''
  });

  const handleSeccionChange = (index, newSeccion) => {
    const newSecciones = [...formData.secciones];
    newSecciones[index] = newSeccion;
    setFormData({ ...formData, secciones: newSecciones });
  };

  const addSeccion = () => {
    setFormData({
      ...formData,
      secciones: [...formData.secciones, { titulo: '', items: [''] }]
    });
  };

  const removeSeccion = (index) => {
    if (formData.secciones.length > 1) {
      const newSecciones = formData.secciones.filter((_, i) => i !== index);
      setFormData({ ...formData, secciones: newSecciones });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar
    if (!formData.nombre_version.trim()) {
      toast.error('El nombre de la versión es requerido');
      return;
    }
    
    // Validar que cada sección tenga título y al menos un item
    for (let i = 0; i < formData.secciones.length; i++) {
      const seccion = formData.secciones[i];
      if (!seccion.titulo.trim()) {
        toast.error(`La sección ${i + 1} necesita un título`);
        return;
      }
      if (!seccion.items.some(item => item.trim())) {
        toast.error(`La sección "${seccion.titulo}" necesita al menos un punto`);
        return;
      }
    }
    
    // Limpiar items vacíos
    const seccionesLimpias = formData.secciones.map(seccion => ({
      ...seccion,
      items: seccion.items.filter(item => item.trim())
    }));
    
    onSave({ ...formData, secciones: seccionesLimpias });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="text-[#D4AF37]" size={24} />
            <h2 className="text-xl font-semibold text-white">
              {version ? 'Editar Versión' : 'Nueva Versión de Condiciones'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <X size={20} className="text-zinc-400" />
          </button>
        </div>
        
        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Datos básicos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Nombre de la Versión *</label>
                <input
                  type="text"
                  value={formData.nombre_version}
                  onChange={(e) => setFormData({ ...formData, nombre_version: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="Ej: v1.0, v2.0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Fecha de Vigencia *</label>
                <input
                  type="date"
                  value={formData.fecha_vigencia}
                  onChange={(e) => setFormData({ ...formData, fecha_vigencia: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Título del Documento</label>
                <input
                  type="text"
                  value={formData.titulo_documento}
                  onChange={(e) => setFormData({ ...formData, titulo_documento: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>
            
            {/* Secciones */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-zinc-400">Secciones del Documento</label>
                <button
                  type="button"
                  onClick={addSeccion}
                  className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1"
                >
                  <Plus size={14} /> Agregar sección
                </button>
              </div>
              
              <div className="space-y-3">
                {formData.secciones.map((seccion, index) => (
                  <SeccionEditor
                    key={index}
                    seccion={seccion}
                    index={index}
                    onChange={handleSeccionChange}
                    onRemove={removeSeccion}
                    canRemove={formData.secciones.length > 1}
                  />
                ))}
              </div>
            </div>
            
            {/* Notas internas */}
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Notas Internas (no visibles al cliente)</label>
              <textarea
                value={formData.notas_internas}
                onChange={(e) => setFormData({ ...formData, notas_internas: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37] resize-none"
                rows={2}
                placeholder="Notas o comentarios para el equipo..."
              />
            </div>
          </div>
        </form>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-800 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-6 py-2 bg-[#D4AF37] hover:bg-[#B8960F] text-black font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save size={18} />
                {version ? 'Guardar Cambios' : 'Crear Versión'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Componente principal
export default function CondicionesVentaAdmin() {
  const { token } = useAuth();
  const [versiones, setVersiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);

  const fetchVersiones = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/condiciones-venta/versiones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersiones(response.data.versiones);
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast.error('Error al cargar las versiones');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVersiones();
  }, [fetchVersiones]);

  const handleCreate = () => {
    setEditingVersion(null);
    setShowModal(true);
  };

  const handleEdit = (version) => {
    setEditingVersion(version);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingVersion) {
        await axios.put(
          `${API_URL}/api/condiciones-venta/versiones/${editingVersion.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Versión actualizada exitosamente');
      } else {
        await axios.post(
          `${API_URL}/api/condiciones-venta/versiones`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Versión creada exitosamente');
      }
      setShowModal(false);
      fetchVersiones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleActivar = async (version) => {
    try {
      await axios.post(
        `${API_URL}/api/condiciones-venta/versiones/${version.id}/activar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Versión "${version.nombre_version}" activada como vigente`);
      fetchVersiones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar');
    }
  };

  const handleDelete = async (version) => {
    if (!window.confirm(`¿Eliminar la versión "${version.nombre_version}"?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      await axios.delete(
        `${API_URL}/api/condiciones-venta/versiones/${version.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Versión eliminada');
      fetchVersiones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleInicializar = async () => {
    if (!window.confirm('¿Crear la versión inicial de condiciones basada en el texto actual?\n\nEsto solo es necesario la primera vez.')) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/condiciones-venta/inicializar-default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.mensaje);
      fetchVersiones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al inicializar');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copiada al portapapeles');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
          <p className="text-zinc-400">Cargando versiones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white flex items-center gap-3">
              <Shield className="text-[#D4AF37]" />
              Condiciones de Venta
            </h1>
            <p className="text-zinc-400 mt-1">
              Gestiona las versiones de las condiciones de venta, garantía y retracto
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] hover:bg-[#B8960F] text-black font-medium rounded-lg transition-colors"
          >
            <Plus size={18} />
            Nueva Versión
          </button>
        </div>

        {/* Info card si no hay versiones */}
        {versiones.length === 0 && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 text-center">
            <AlertCircle className="mx-auto text-[#D4AF37] mb-4" size={48} />
            <h3 className="text-lg font-medium text-white mb-2">No hay versiones de condiciones</h3>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              Crea tu primera versión de condiciones de venta, garantía y retracto. 
              Puedes inicializar con el texto actual o crear una nueva desde cero.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={handleInicializar}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <History size={18} />
                Inicializar con Texto Actual
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-[#D4AF37] hover:bg-[#B8960F] text-black font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <Plus size={18} />
                Crear Nueva
              </button>
            </div>
          </div>
        )}

        {/* Lista de versiones */}
        {versiones.length > 0 && (
          <div className="space-y-4">
            {versiones.map((version) => (
              <div 
                key={version.id}
                className={`bg-zinc-900/50 border rounded-xl overflow-hidden transition-all ${
                  version.activa ? 'border-[#D4AF37]' : 'border-zinc-800'
                }`}
              >
                {/* Header de versión */}
                <div className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Indicador activo */}
                    <div className={`w-3 h-3 rounded-full ${version.activa ? 'bg-green-500' : 'bg-zinc-600'}`} />
                    
                    {/* Info principal */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{version.nombre_version}</span>
                        {version.activa && (
                          <span className="text-xs px-2 py-0.5 bg-green-900/50 text-green-400 rounded-full">
                            VIGENTE
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-zinc-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          Vigente desde: {version.fecha_vigencia}
                        </span>
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {version.created_by_nombre}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Acciones */}
                  <div className="flex items-center gap-2">
                    {!version.activa && (
                      <button
                        onClick={() => handleActivar(version)}
                        className="px-3 py-1.5 bg-green-900/30 hover:bg-green-900/50 text-green-400 text-sm rounded-lg transition-colors flex items-center gap-1"
                        title="Activar como vigente"
                      >
                        <CheckCircle size={16} />
                        Activar
                      </button>
                    )}
                    <a
                      href={version.url_publica}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-lg transition-colors"
                      title="Ver página pública"
                    >
                      <ExternalLink size={18} />
                    </a>
                    <button
                      onClick={() => copyUrl(version.url_publica)}
                      className="p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-lg transition-colors"
                      title="Copiar enlace"
                    >
                      <Copy size={18} />
                    </button>
                    <button
                      onClick={() => handleEdit(version)}
                      className="p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit size={18} />
                    </button>
                    {!version.activa && (
                      <button
                        onClick={() => handleDelete(version)}
                        className="p-2 hover:bg-red-900/30 text-zinc-400 hover:text-red-400 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => setExpandedVersion(expandedVersion === version.id ? null : version.id)}
                      className="p-2 hover:bg-zinc-800 text-zinc-400 rounded-lg transition-colors"
                    >
                      {expandedVersion === version.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                </div>
                
                {/* Contenido expandido */}
                {expandedVersion === version.id && (
                  <div className="px-6 pb-4 border-t border-zinc-800">
                    <div className="pt-4 space-y-4">
                      {/* URL pública */}
                      <div className="bg-zinc-800/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-sm text-zinc-400 mb-1">
                          <Link2 size={14} />
                          Enlace público para clientes:
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-sm text-[#D4AF37] bg-zinc-900 px-3 py-1.5 rounded overflow-x-auto">
                            {version.url_publica}
                          </code>
                          <button
                            onClick={() => copyUrl(version.url_publica)}
                            className="p-2 hover:bg-zinc-700 text-zinc-400 rounded"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Secciones */}
                      <div>
                        <h4 className="text-sm text-zinc-400 mb-2">Contenido ({version.secciones?.length || 0} secciones):</h4>
                        <div className="space-y-3">
                          {version.secciones?.map((seccion, idx) => (
                            <div key={idx} className="bg-zinc-800/30 rounded-lg p-3">
                              <h5 className="text-[#D4AF37] font-medium text-sm mb-2">{seccion.titulo}</h5>
                              <ul className="space-y-1">
                                {seccion.items.slice(0, 2).map((item, itemIdx) => (
                                  <li key={itemIdx} className="text-sm text-zinc-400 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-[#D4AF37]">
                                    {item.length > 100 ? item.substring(0, 100) + '...' : item}
                                  </li>
                                ))}
                                {seccion.items.length > 2 && (
                                  <li className="text-xs text-zinc-500 pl-4">
                                    +{seccion.items.length - 2} puntos más...
                                  </li>
                                )}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Notas internas */}
                      {version.notas_internas && (
                        <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-sm text-amber-400 mb-1">
                            <AlertCircle size={14} />
                            Notas internas:
                          </div>
                          <p className="text-sm text-amber-200/70">{version.notas_internas}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Modal */}
        {showModal && (
          <VersionModal
            version={editingVersion}
            onClose={() => setShowModal(false)}
            onSave={handleSave}
            isLoading={saving}
          />
        )}
      </div>
    </div>
  );
}
